import React, { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Users, Clock, Calendar, GraduationCap, UserCheck, BookOpen,
  Receipt, Megaphone, BarChart3, Bell, Layers, Settings as SettingsIcon,
  LogOut, Menu, X, Moon, Sun,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const NAV = [
  { name: "Dashboard", path: "/dashboard", icon: LayoutDashboard, roles: ["admin", "sales", "reception", "trainer", "marketing"] },
  { name: "Leads", path: "/leads", icon: Users, roles: ["admin", "sales", "reception", "marketing"], module: "leads" },
  { name: "Follow-ups", path: "/follow-ups", icon: Clock, roles: ["admin", "sales", "reception"], module: "leads" },
  { name: "Appointments", path: "/appointments", icon: Calendar, roles: ["admin", "sales", "reception"], module: "appointments" },
  { name: "Admissions", path: "/admissions", icon: GraduationCap, roles: ["admin", "sales", "reception"], module: "admissions" },
  { name: "Students", path: "/students", icon: UserCheck, roles: ["admin", "trainer", "sales", "reception", "marketing"], module: "students" },
  { name: "Batches", path: "/batches", icon: BookOpen, roles: ["admin", "trainer", "sales", "reception", "marketing"], module: "batches" },
  { name: "Fees", path: "/fees", icon: Receipt, roles: ["admin", "sales", "reception"], module: "fees" },
  { name: "Marketing", path: "/marketing", icon: Megaphone, roles: ["admin", "reception", "marketing"], module: "marketing" },
  { name: "Reports", path: "/reports", icon: BarChart3, roles: ["admin"] },
  { name: "Notifications", path: "/notifications", icon: Bell, roles: ["admin", "sales", "reception", "trainer", "marketing"] },
  { name: "Integrations", path: "/integrations", icon: Layers, roles: ["admin"] },
  { name: "Settings", path: "/settings", icon: SettingsIcon, roles: ["admin"] },
];

const ROLE_LABEL = { admin: "Admin / Owner", sales: "Sales Counsellor", reception: "Reception", trainer: "Trainer", marketing: "Marketing Executive" };

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));

  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const { data: notif } = useQuery({
    queryKey: ["unread"], queryFn: async () => (await api.get("/notifications/unread-count")).data,
    refetchInterval: 20000,
  });
  const { data: myPerms } = useQuery({
    queryKey: ["my-permissions"], queryFn: async () => (await api.get("/settings/my-permissions")).data,
  });

  const allowedModules = myPerms?.modules || [];
  const items = NAV.filter(
    (n) => n.roles.includes(user?.role) &&
      (user?.role === "admin" || !n.module || allowedModules.includes(n.module))
  );
  const brand = settings?.academy_name || "LaunchX Academy";
  const logo = settings?.logo_data_url;

  const toggleDark = () => {
    document.documentElement.classList.toggle("dark");
    setDark(document.documentElement.classList.contains("dark"));
  };

  const doLogout = async () => { await logout(); navigate("/login"); };

  const SidebarInner = () => (
    <div className="flex flex-col h-full">
      <div className="px-5 py-5 border-b border-slate-200 dark:border-slate-800 flex items-center gap-3">
        {logo ? (
          <img src={logo} alt="logo" className="h-9 w-9 rounded object-contain bg-white" data-testid="brand-logo" />
        ) : (
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-blue-700 to-indigo-800 text-white grid place-items-center font-head font-extrabold">L</div>
        )}
        <div className="leading-tight">
          <div className="font-head font-extrabold text-slate-900 dark:text-white" data-testid="brand-name">{brand}</div>
          <div className="text-[11px] text-orange-600 font-semibold uppercase tracking-wider">CRM • Rampur</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-1">
        {items.map((n) => (
          <NavLink
            key={n.path}
            to={n.path}
            data-testid={`nav-${n.name.toLowerCase().replace(/[^a-z]/g, "-")}`}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? "bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300 border-l-4 border-blue-600"
                  : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
              }`
            }
          >
            <n.icon className="w-[18px] h-[18px]" />
            <span>{n.name}</span>
            {n.name === "Notifications" && notif?.unread > 0 && (
              <Badge className="ml-auto bg-orange-600 hover:bg-orange-600">{notif.unread}</Badge>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-slate-200 dark:border-slate-800">
        <div className="px-2 py-2 mb-2">
          <div className="text-sm font-semibold truncate">{user?.name}</div>
          <div className="text-xs text-slate-500">{ROLE_LABEL[user?.role]}</div>
        </div>
        <Button variant="outline" className="w-full justify-start gap-2" onClick={doLogout} data-testid="logout-btn">
          <LogOut className="w-4 h-4" /> Logout
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 sticky top-0 h-screen">
        <SidebarInner />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-72 bg-white dark:bg-slate-900 shadow-xl">
            <SidebarInner />
          </div>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 bg-white/90 dark:bg-slate-900/90 backdrop-blur-md border-b border-slate-200 dark:border-slate-800 sticky top-0 z-40 flex items-center gap-3 px-4">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="mobile-menu-btn">
            <Menu className="w-6 h-6" />
          </button>
          <div className="font-head font-bold text-lg lg:hidden">{brand}</div>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleDark} data-testid="theme-toggle">
              {dark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <button className="relative" onClick={() => navigate("/notifications")} data-testid="header-bell">
              <Bell className="w-5 h-5 text-slate-600 dark:text-slate-300" />
              {notif?.unread > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-orange-600 text-white text-[10px] rounded-full w-4 h-4 grid place-items-center">
                  {notif.unread > 9 ? "9+" : notif.unread}
                </span>
              )}
            </button>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 max-w-[1400px] w-full mx-auto animate-in-up">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
